/**
 * Network utilities for consistent error handling across all modules
 */

// Consistent error messages for all modules (English + Spanish)
export const NETWORK_ERRORS = {
  en: {
    offline: 'No internet connection. Please check your WiFi or data.',
    timeout: 'Connection timed out. Please try again.',
    serverError: 'Server error. Please try again later.',
    unauthorized: 'Session expired. Please log in again.',
    unknown: 'Something went wrong. Please try again.',
    savedOffline: 'Saved offline. Will sync when connected.',
    syncFailed: 'Failed to sync. Will retry when connected.',
    retrying: 'Connection issue. Retrying...',
  },
  es: {
    offline: 'Sin conexión a internet. Verifica tu WiFi o datos.',
    timeout: 'Conexión agotada. Intenta de nuevo.',
    serverError: 'Error del servidor. Intenta más tarde.',
    unauthorized: 'Sesión expirada. Inicia sesión de nuevo.',
    unknown: 'Algo salió mal. Intenta de nuevo.',
    savedOffline: 'Guardado sin conexión. Se sincronizará al conectar.',
    syncFailed: 'Error al sincronizar. Se reintentará al conectar.',
    retrying: 'Problema de conexión. Reintentando...',
  }
} as const;

export type NetworkErrorKey = keyof typeof NETWORK_ERRORS.en;

/**
 * Get localized network error message
 */
export function getNetworkError(key: NetworkErrorKey, language: 'en' | 'es' = 'en'): string {
  return NETWORK_ERRORS[language][key];
}

/**
 * Check if error is a network/offline error
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('failed to fetch') ||
      message.includes('networkerror') ||
      message.includes('timeout') ||
      message.includes('aborted') ||
      message.includes('err_internet_disconnected') ||
      message.includes('err_network') ||
      error.name === 'AbortError' ||
      error.name === 'TypeError' // fetch throws TypeError when offline
    );
  }
  return false;
}

/**
 * Check if error is a timeout error
 */
export function isTimeoutError(error: unknown): boolean {
  if (error instanceof Error) {
    return error.name === 'AbortError' || error.message.toLowerCase().includes('timeout');
  }
  return false;
}

/**
 * Get appropriate error message based on error type
 */
export function getErrorMessage(error: unknown, language: 'en' | 'es' = 'en'): string {
  if (!navigator.onLine) {
    return getNetworkError('offline', language);
  }

  if (isTimeoutError(error)) {
    return getNetworkError('timeout', language);
  }

  if (isNetworkError(error)) {
    return getNetworkError('offline', language);
  }

  if (error instanceof Error) {
    // Check for specific HTTP status codes in error message
    if (error.message.includes('401') || error.message.includes('unauthorized')) {
      return getNetworkError('unauthorized', language);
    }
    if (error.message.includes('500') || error.message.includes('server')) {
      return getNetworkError('serverError', language);
    }
    // Return the original error message if it's meaningful
    if (error.message && !error.message.includes('fetch')) {
      return error.message;
    }
  }

  return getNetworkError('unknown', language);
}

interface FetchWithTimeoutOptions extends RequestInit {
  timeout?: number;
  retries?: number;       // auto-retries on timeout/network error (GET/HEAD only)
  retryDelayMs?: number;
}

/**
 * Fetch wrapper with timeout + auto-retry.
 *
 * Why retry: when an iOS PWA is backgrounded, its network socket is suspended.
 * The FIRST request after you reopen the app fires against that stale socket and
 * hangs until it times out — even though wifi is fine (a manual refresh works
 * instantly because it re-establishes the connection). Retrying on a fresh fetch
 * makes that hang invisible. Only idempotent methods (GET/HEAD) auto-retry, so a
 * POST that may have reached the server (clock-in/out, ticket submit) never
 * double-fires.
 */
export async function fetchWithTimeout(
  url: string,
  options: FetchWithTimeoutOptions = {}
): Promise<Response> {
  const { timeout = 12000, retries = 2, retryDelayMs = 500, ...fetchOptions } = options;
  const method = (fetchOptions.method || 'GET').toUpperCase();
  const idempotent = method === 'GET' || method === 'HEAD';

  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(url, { ...fetchOptions, signal: controller.signal });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      lastError = error;
      // Retry only transient failures (abort/timeout or network drop) on safe
      // methods. fetchWithTimeout only throws on network/abort — an HTTP error
      // status returns a Response and never lands here, so this is safe.
      const transient = (error as any)?.name === 'AbortError' || error instanceof TypeError;
      if (attempt < retries && idempotent && transient) {
        await new Promise(r => setTimeout(r, retryDelayMs));
        continue;
      }
      throw error;
    }
  }
  throw lastError;
}

/**
 * API call wrapper with consistent error handling
 */
export async function apiCallWithErrorHandling<T>(
  url: string,
  options: FetchWithTimeoutOptions = {},
  language: 'en' | 'es' = 'en'
): Promise<{ success: boolean; data?: T; error?: string }> {
  // Check online status first
  if (!navigator.onLine) {
    return { success: false, error: getNetworkError('offline', language) };
  }

  try {
    const token = localStorage.getItem('bfa_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...options.headers
    };

    const response = await fetchWithTimeout(url, {
      ...options,
      headers
    });

    // Handle 401 - session expired
    if (response.status === 401) {
      localStorage.removeItem('bfa_token');
      localStorage.removeItem('bfa_user');
      return { success: false, error: getNetworkError('unauthorized', language) };
    }

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || data.message || getNetworkError('serverError', language)
      };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: getErrorMessage(error, language) };
  }
}
